import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Sparkles, Trophy } from 'lucide-react'
import useCountdown from '../../hooks/useCountdown'
import { HALLOWEEN_POSTER, formatEventTimer, formatMoscowDate } from '../../utils/halloween'
import { CountdownTiles, PumpkinIcon } from './shared'

/**
 * Герой эвента: постер, название и главный таймер текущей фазы.
 * Таймеры — секунды из ответа, тикают локально (useCountdown).
 */
const HalloweenHero = ({ phase, endsIn, drawIn, phaseEndsIn }) => {
  const { t, i18n } = useTranslation()
  const endsRemaining = useCountdown(phase === 'active' ? endsIn : null)
  const drawRemaining = useCountdown(phase === 'soon_draw' ? drawIn : null)
  const hideRemaining = useCountdown(phase === 'results' ? phaseEndsIn : null)

  // Сбор идёт до 00:00 МСК следующего дня — последний день показываем на секунду раньше
  const endDate = useMemo(
    () => formatMoscowDate(endsIn, i18n.language, { shiftSeconds: -1 }),
    [endsIn, i18n.language]
  )
  const drawDate = useMemo(
    () => formatMoscowDate(drawIn, i18n.language, { withTime: true }),
    [drawIn, i18n.language]
  )

  return (
    <section className="relative overflow-hidden rounded-3xl border border-orange-400/20 bg-[#0d0a12] shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_25px_50px_-12px_rgba(255,106,0,0.18)]">
      <div className="relative">
        <img
          src={HALLOWEEN_POSTER}
          alt={t('halloweenEvent.title')}
          className="aspect-[4/3] w-full object-cover"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0d0a12] via-[#0d0a12]/70 to-transparent"
          aria-hidden="true"
        />
        <div className="absolute inset-x-0 bottom-0 px-5 pb-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-orange-400/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-orange-200">
            <PumpkinIcon size={14} glow />
            {t('halloweenEvent.badge')}
          </span>
          <h1 className="hev-flame-text mt-2 text-4xl leading-[0.95] lg:text-5xl">
            {t('halloweenEvent.title')}
          </h1>
        </div>
      </div>

      <div className="relative px-5 pb-5 pt-2">
        <p className="text-sm leading-relaxed text-white/80">{t('halloweenEvent.tagline')}</p>

        {phase === 'active' && (
          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-orange-300">
              {t('halloweenEvent.hero.untilEnd')}
            </p>
            <div className="mt-2">
              <CountdownTiles seconds={endsRemaining} />
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-text-secondary">
              <CalendarClock size={13} className="shrink-0 text-orange-300/80" />
              {t('halloweenEvent.hero.schedule', { end: endDate, draw: drawDate })}
            </p>
          </div>
        )}

        {phase === 'soon_draw' && (
          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-violet-300">
              {t('halloweenEvent.hero.untilDraw')}
            </p>
            <div className="mt-2">
              <CountdownTiles seconds={drawRemaining} tone="violet" />
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-text-secondary">
              <CalendarClock size={13} className="shrink-0 text-violet-300/80" />
              {t('halloweenEvent.hero.drawOn', { date: drawDate })}
            </p>
          </div>
        )}

        {phase === 'drawing' && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-2">
            <Sparkles size={16} className="animate-pulse text-violet-300" />
            <span className="text-sm font-bold text-violet-100">{t('halloweenEvent.draw.pulling')}</span>
          </div>
        )}

        {phase === 'results' && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-yellow-400/30 bg-yellow-400/10 px-3 py-1.5 text-sm font-bold text-yellow-200">
              <Trophy size={14} />
              {t('halloweenEvent.draw.titleResults')}
            </span>
            <span className="text-xs tabular-nums text-text-secondary">
              {t('halloweenEvent.hero.hideIn', { time: formatEventTimer(hideRemaining, t) })}
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

export default HalloweenHero
